<template>
  <panel-layout
    v-if="canEnrol"
    id="panel"
    :loading="loading"
    :didAttemptToDismiss="cancel"
  >
    <template #heading>
      <div>
        <h2 class="text-lg md:text-xl tracking-tight font-bold leading-none/85 mb-1">
          {{ $t('pages.customer.course.add.heading') }}
        </h2>
      </div>
    </template>

    <div class="h-full">
      <o-scroll class="px-5 py-6">
        <o-form ref="form">
          <o-field-group
            v-if="!isCustomerLocked"
            v-slot="{ status }"
            :label="$t('pages.customer.course.add.selectCustomer')"
            :name="formFieldCustomer"
            class="mb-4 block"
            rules="required"
          >
            <customer-select v-model="customer" :status="status" create />
          </o-field-group>

          <o-field-group
            v-else
            :label="$t('general.customer')"
            class="mb-4 block"
          >
            <div v-if="loadingCustomer" class="flex items-center p-3 border rounded-lg dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
              <o-loader :scale="0.5" class="mr-3 h-4 w-4" />
              <span class="text-gray-500">{{ $t('general.loading') }}</span>
            </div>
            <div v-else-if="customer" class="flex items-center p-3 border rounded-lg dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
              <o-avatar :items="[customer]" size="md" border="" class="flex-shrink-0" />
              <div class="ml-3">
                <p class="font-medium leading-none">
                  {{ customer.firstName }} {{ customer.lastName }}
                </p>
                <p v-if="customer.email" class="mt-1 text-gray-600 dark:text-gray-400 text-sm leading-none">
                  {{ customer.email }}
                </p>
              </div>
            </div>
          </o-field-group>

          <o-field-group
            v-if="!isCourseRunLocked"
            v-slot="{ status }"
            :label="$t('pages.customer.course.add.selectCourse')"
            :name="formFieldCourse"
            class="mb-4 block"
            rules="required"
          >
            <list-box-field
              v-model="courseRun"
              :options="courseRunOptions"
              :placeholder="$t('pages.customer.course.add.selectCoursePlaceholder')"
              :status="status"
              :multi="false"
              search
            >
              <template #value>
                <span v-if="courseRun" class="truncate">{{ courseRun.course?.name }} - {{ courseRun.information }}</span>
              </template>
              <template #option="{ option }">
                <div class="w-full flex items-center justify-between">
                  <div>
                    <p class="text-gray-800 dark:text-gray-100 font-medium">{{ option.value.course?.name }}</p>
                    <p class="text-sm text-gray-500">{{ option.value.information }}</p>
                  </div>
                  <div class="flex flex-col items-end space-y-1 ml-3 flex-shrink-0">
                    <span v-if="option.value.currentPrice?.price || option.value.paymentConfig?.price" class="text-sm font-semibold text-primary-600 dark:text-primary-400">
                      {{ $currency.format(option.value.currentPrice?.price || option.value.paymentConfig?.price) }}
                    </span>
                    <span v-if="option.info" class="text-xs text-gray-400">
                      {{ option.info }}
                    </span>
                  </div>
                </div>
              </template>
            </list-box-field>
          </o-field-group>

          <o-field-group
            v-else
            :label="$t('general.course')"
            class="mb-4 block"
          >
            <div v-if="loadingCourseRun" class="flex items-center p-3 border rounded-lg dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
              <o-loader :scale="0.5" class="mr-3 h-4 w-4" />
              <span class="text-gray-500">{{ $t('general.loading') }}</span>
            </div>
            <div v-else-if="courseRun" class="flex items-center justify-between p-3 border rounded-lg dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
              <div>
                <p class="font-medium leading-none">
                  {{ courseRun.course?.name }}
                </p>
                <p class="mt-1 text-gray-600 dark:text-gray-400 text-sm leading-none">
                  {{ courseRun.information }}
                </p>
              </div>
              <div class="flex items-center space-x-1">
                <o-chip v-if="courseRun.dates?.[0]" variant="primary" size="sm">
                  {{ $t('components.courseRun.elements.select.starts') }} {{ $date.convert(courseRun.dates[0], undefined, $date.presets.long) }}
                </o-chip>
              </div>
            </div>
          </o-field-group>

          <!-- Pricing Scheme Selection -->
          <div v-if="courseRun && hasMultipleSchemes" class="mb-4 p-4 border rounded-lg dark:border-gray-800">
            <h4 class="font-medium mb-3">
              {{ $t('pages.customer.course.add.selectScheme') }}
            </h4>

            <div class="space-y-2">
              <label
                v-for="option in schemeOptions"
                :key="option.schemeCode"
                class="flex items-center justify-between p-3 border rounded-lg cursor-pointer dark:border-gray-700"
                :class="{ 'border-primary-500 bg-primary-50 dark:bg-primary-900/20': scheme === option.schemeCode }"
              >
                <div class="flex items-center">
                  <input
                    v-model="scheme"
                    type="radio"
                    :value="option.schemeCode"
                    class="mr-3"
                    @change="applyScheme(option.schemeCode)"
                  >
                  <p class="font-medium">
                    {{ option.schemeName }}
                  </p>
                </div>
                <div class="flex items-center space-x-2">
                  <o-chip v-if="option.isEarly" size="sm" flat variant="info">
                    {{ $t('general.earlyBird') }}
                  </o-chip>
                  <span class="text-sm font-semibold">
                    {{ $currency.format(option.price) }}
                  </span>
                </div>
              </label>
            </div>
          </div>

          <!-- Pricing Section -->
          <div v-if="courseRun && !idOrder" class="mb-4 p-4 border rounded-lg dark:border-gray-800">
            <h4 class="font-medium mb-3">
              {{ $t('pages.customer.course.add.pricing') }}
            </h4>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <o-field-group :label="$t('general.price')" class="block">
                <currency-field v-model="price">
                  <template v-if="hasEarlyBirdConfig && !hasMultipleSchemes" #trailing>
                    <div class="flex items-center pl-2 border-l border-gray-200 dark:border-gray-700">
                      <toggle-field v-model="isEarly" class="mr-2" />
                      <span class="text-xs text-gray-500 whitespace-nowrap">{{ $t('general.earlyBird') }}</span>
                    </div>
                  </template>
                </currency-field>
              </o-field-group>

              <o-field-group :label="$t('general.deposit')" class="block">
                <currency-field v-model="deposit" />
              </o-field-group>
            </div>
          </div>

          <!-- Payment Options -->
          <div v-if="courseRun" class="mb-4 p-4 border rounded-lg dark:border-gray-800">
            <h4 class="font-medium mb-3">
              {{ $t('pages.customer.course.add.paymentOption') }}
            </h4>

            <div class="space-y-3">
              <!-- Pay Now -->
              <label
                class="flex items-start p-3 border rounded-lg cursor-pointer dark:border-gray-700"
                :class="{ 'border-primary-500 bg-primary-50 dark:bg-primary-900/20': paymentOption === 'payNow' }"
              >
                <input v-model="paymentOption" type="radio" value="payNow" class="mt-1 mr-3">
                <div>
                  <p class="font-medium">
                    {{ $t('pages.customer.course.add.payNow') }}
                  </p>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    {{ $t('pages.customer.course.add.payNowInfo') }}
                  </p>
                </div>
              </label>

              <!-- Request Payment -->
              <label
                class="flex items-start p-3 border rounded-lg cursor-pointer dark:border-gray-700"
                :class="{ 'border-primary-500 bg-primary-50 dark:bg-primary-900/20': paymentOption === 'request' }"
              >
                <input v-model="paymentOption" type="radio" value="request" class="mt-1 mr-3">
                <div class="flex-1">
                  <p class="font-medium">
                    {{ $t('pages.customer.course.add.requestPayment') }}
                  </p>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    {{ $t('pages.customer.course.add.requestPaymentInfo') }}
                  </p>

                  <div v-if="paymentOption === 'request'" class="mt-3 flex items-center space-x-4">
                    <label class="flex items-center cursor-pointer">
                      <input v-model="requestChannels" type="checkbox" value="email" class="mr-2">
                      {{ $t('general.email') }}
                    </label>
                    <label class="flex items-center cursor-pointer">
                      <input v-model="requestChannels" type="checkbox" value="sms" class="mr-2">
                      {{ $t('general.sms') }}
                    </label>
                  </div>
                </div>
              </label>

              <!-- Pay Later -->
              <label
                class="flex items-start p-3 border rounded-lg cursor-pointer dark:border-gray-700"
                :class="{ 'border-primary-500 bg-primary-50 dark:bg-primary-900/20': paymentOption === 'none' }"
              >
                <input v-model="paymentOption" type="radio" value="none" class="mt-1 mr-3">
                <div>
                  <p class="font-medium">
                    {{ $t('pages.customer.course.add.payLater') }}
                  </p>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    {{ $t('pages.customer.course.add.payLaterInfo') }}
                  </p>
                </div>
              </label>
            </div>
          </div>

          <o-field-group
            v-if="courseRun"
            :label="upcomingDatesLabel"
            class="mb-4 block"
            rules="required"
          >
            <div class="border rounded-lg">
              <div v-if="loadingReservations" class="flex items-center justify-center p-4">
                <o-loader :scale="0.5" class="mr-3 h-4 w-4" />
                <span class="text-gray-500">{{ $t('general.loading') }}</span>
              </div>
              <template v-else>
                <div v-for="courseRunReservation in courseRunReservations" :key="courseRunReservation.id" class="p-3 border-b last:border-none">
                  <div class="flex items-center justify-between">
                    <div>
                      <p class="font-semibold">
                        {{ $t('pages.customer.course.add.withEmployee', { services: courseRunReservation.reservation._servicesLabel, employees: courseRunReservation.reservation.employeesLabel }) }}
                      </p>
                      <p class="font-normal text-sm">
                        {{ courseRunReservation.reservation.dateLabel }}
                      </p>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </o-field-group>
        </o-form>
      </o-scroll>
    </div>

    <template #footer>
      <div class="flex justify-between">
        <div>
          <o-button flat outline @click="cancel()">
            {{ $t('general.cancel') }}
          </o-button>
        </div>

        <div>
          <o-button variant="primary" icon="save" :disabled="disabled" @click="save()">
            {{ $t('pages.course.view.enrolCustomer') }}
          </o-button>
        </div>
      </div>
    </template>
  </panel-layout>
</template>

<script setup lang="ts">
import debounce from 'lodash/debounce'
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

// @ts-ignore
import CustomerSelect from '@/components/customer/elements/Select.vue'
// @ts-ignore
import { ListBoxField, CurrencyField, ToggleField } from '@/components/fields'
import { OAvatar, OLoader } from '@/components/elements'

import { usePanel } from '~/composables/usePanel'

const props = withDefaults(defineProps<{
  idCustomer?: string
  idCourseRun?: string
  idOrder?: string
  presets?: Record<string, any>
}>(), {
  presets: () => ({}),
})

const emit = defineEmits<{
  (e: 'update:loading', value: boolean): void
  (e: 'update:order', value: any): void
  (e: 'success', response: any): void
  (e: 'error'): void
}>()

const { t } = useI18n()
const { $api, $bus, $date, $notification, $gates, $grants } = useNuxtApp()

const panel = usePanel()

const formFieldCustomer = 'Customer'
const formFieldCourse = 'Course'
const customer = ref<any>(null)
const courseRun = ref<any>(null)
const courseRuns = ref<any[]>([])
const courseRunReservations = ref<any[]>([])
const loading = ref(false)
const loadingCustomer = ref(false)
const loadingCourseRun = ref(false)
const loadingReservations = ref(false)
const scheme = ref<string | null>(null)
const price = ref<number | null>(null)
const deposit = ref<number | null>(null)
const isEarly = ref<boolean | null>(null)
const paymentOption = ref('none')
const requestChannels = ref<string[]>(['email'])

const canEnrol = computed(() => $gates.get('courses') && $grants.all({ customerCourse: ['create'] }))
const disabled = computed(() => !canEnrol.value || !customer.value || !courseRun.value)
const query = computed(() => ({
  idCustomer: props.idCustomer,
  idCourseRun: props.idCourseRun,
}))
const isCustomerLocked = computed(() => !!(props.presets.customer || query.value.idCustomer))
const isCourseRunLocked = computed(() => !!(props.presets.courseRun || query.value.idCourseRun))
const courseRunOptions = computed(() => courseRuns.value.map(run => ({
  value: run,
  label: `${run.course?.name || 'Course'} - ${run.information}`,
  info: run.dates?.[0] ? $date.convert(run.dates[0], undefined, $date.presets.short) : '',
})))
const upcomingDatesLabel = computed(() => {
  if (!courseRun.value) {
    return ''
  }
  return t('pages.customer.course.add.upcomingDatesFor', {
    name: courseRun.value.course.name,
    label: courseRun.value.information,
  })
})
const schemeOptions = computed(() => courseRun.value?.currentPrice?.schemes || [])
const hasMultipleSchemes = computed(() => schemeOptions.value.length > 1)
const hasEarlyBirdConfig = computed(() => !!courseRun.value?.currentPrice?.earlyPrice)
const isEarlyBirdActive = computed(() => !!courseRun.value?.currentPrice?.isEarly)
const defaultPrice = computed(() => {
  if (isEarly.value) {
    return courseRun.value?.currentPrice?.earlyPrice ?? 0
  }
  return courseRun.value?.currentPrice?.fullPrice ?? 0
})

watch(courseRun, (value) => {
  if (value) {
    fetchCourseRunReservations()
    initializePricing()
  }
})

watch(isEarly, (_newValue, oldValue) => {
  if (oldValue !== null && !hasMultipleSchemes.value) {
    price.value = defaultPrice.value
  }
})

onMounted(async () => {
  if (!canEnrol.value) {
    await panel.close(true)
    return
  }

  if (props.presets.customer) {
    customer.value = props.presets.customer
  } else if (query.value.idCustomer) {
    await fetchCustomer(query.value.idCustomer)
  }

  if (props.presets.courseRun) {
    courseRun.value = props.presets.courseRun
  } else if (query.value.idCourseRun) {
    await fetchCourseRun(query.value.idCourseRun)
  } else {
    fetchCourseRuns()
  }
})

function initializePricing () {
  const currentPrice = courseRun.value?.currentPrice

  if (hasMultipleSchemes.value) {
    const defaultCode = currentPrice?.schemeCode || schemeOptions.value[0]?.schemeCode
    applyScheme(defaultCode)
    return
  }

  scheme.value = null
  isEarly.value = isEarlyBirdActive.value
  price.value = currentPrice?.price ?? 0
  deposit.value = currentPrice?.deposit ?? 0
}

function applyScheme (code?: string | null) {
  const option = schemeOptions.value.find((item: any) => item.schemeCode === code)
  if (!option) {
    return
  }

  scheme.value = option.schemeCode
  price.value = option.price ?? 0
  deposit.value = option.deposit ?? 0
  isEarly.value = !!option.isEarly
}

async function fetchCustomer (idCustomer: string | number) {
  loadingCustomer.value = true
  try {
    const response = await $api.customer.get(String(idCustomer))
    customer.value = response.data
  } catch (e) {
    console.error(e)
  } finally {
    loadingCustomer.value = false
  }
}

async function fetchCourseRun (idCourseRun: string | number) {
  loadingCourseRun.value = true
  try {
    const response = await $api.course.getRunById(idCourseRun)
    courseRun.value = response.data
  } catch (e) {
    console.error(e)
  } finally {
    loadingCourseRun.value = false
  }
}

async function fetchCourseRuns () {
  try {
    const response = await $api.course.listCourseRuns({
      page: 1,
      filters: {
        status: 'active',
        when: 'current_upcoming',
      },
    })
    courseRuns.value = response.items
  } catch (e) {
    console.error(e)
  }
}

const fetchCourseRunReservations = debounce(async () => {
  loadingReservations.value = true
  try {
    const response = await $api.course.getRunById(courseRun.value.idCourseRun)
    courseRunReservations.value = response.data?.reservations ?? []
  } catch (e: any) {
    console.error(e)
    $notification.error({ errors: e?.errors })
  } finally {
    loadingReservations.value = false
  }
}, 500)

const save = async () => {
  if (!canEnrol.value) {
    return false
  }

  emit('update:loading', true)

  try {
    const isDefaultScheme = !scheme.value || scheme.value === schemeOptions.value[0]?.schemeCode
    const response = await $api.customerCourse.enrol({
      idCourseRun: courseRun.value.idCourseRun,
      idCustomer: customer.value.idCustomer,
      scheme: isDefaultScheme ? null : scheme.value,
      price: price.value,
      deposit: deposit.value,
      isEarly: isEarly.value,
      paymentOption: props.idOrder ? 'none' : paymentOption.value,
      requestChannels: requestChannels.value,
    })

    if (!response.success || !response.data) {
      $notification.error({ errors: response.errors })
      emit('error')
      return false
    }

    const customerCourse = response.data
    $bus.emit.created('CustomerCourse', customerCourse)

    if (props.idOrder) {
      const orderResponse = await $api.order.addCustomerCourses(
        String(props.idOrder),
        [String(customerCourse.idCustomerCourse)],
      )
      const updatedOrder = orderResponse.data

      emit('update:order', updatedOrder)
      $bus.emit.updated('Order', updatedOrder)
    }

    emit('success', customerCourse)

    $notification.toast({
      title: props.idOrder
        ? t('components.order.actions.addCustomerCourses.success')
        : t('pages.customer.course.add.success'),
      text: props.idOrder
        ? t('components.order.actions.addCustomerCourses.successInfo')
        : t('pages.customer.course.add.successInfo'),
      variant: 'success',
    })

    if (!props.idOrder && paymentOption.value === 'payNow' && response.order) {
      panel.replace('order/[id]', { id: String(response.order.idOrder) })
    } else {
      panel.close()
    }

    return true
  } catch (e: any) {
    console.error(e)
    emit('error')
    $notification.error({
      errors: e?.errors,
    })
  } finally {
    emit('update:loading', false)
  }
}

const cancel = (force = false) => {
  panel.close(force)
}
</script>
